import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  signal,
} from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { MatIconModule } from "@angular/material/icon";
import { OmniarkUiModule, SelectOption } from "@corp-ais/omniark-ui";
import { LanguageModeService } from "@app/shared";
import { PermissionService } from "@app/core/services/permission.service";
import { FeatureFlagService } from "@app/core/services/feature-flag.service";
import {
  isRejectableStatus,
  isStageableStatus,
} from "@app/shared/models/enrichment-list.model";
import { CategoryService } from "@app/shared/services/category.service";
import { CatalogService } from "@app/shared/services/catalog.service";
import {
  EnrichmentLocale,
  LocalizedText,
  resolveLocalized,
  ShelfRef,
} from "@app/shared/models/enrichment-detail.model";
import { ProductOfferingService } from "../../../../services/enrichment-data.service";
import { pricesOf } from "../enrichment-list.transformer";
import { EnrichmentPriceRow } from "../enrichment-list.types";
import { readPriceRows } from "../price-rows";
import {
  buildCatalogTree,
  CATEGORY_TYPE_CHAR,
  collectLifeStyleIds,
  flattenCatalog,
  isLifeStyleNode,
  LIFESTYLE_CATEGORY_VALUE,
  ShelfCatalogEntry,
  ShelfCatalogNode,
} from "../../../../utils/category-catalog.util";
import {
  isAiChatbotChannel,
  isEasyAppChannel,
} from "@app/shared/constants/channel.constants";
import { mayKeepLiveOnSave } from "../../../../utils/price-authority.util";
import {
  AssignLiveChoice,
  buildAssignDesignedCopy,
  buildAssignLiveCopy,
  resolveAssignDecision,
} from "../../../edit/enrichment-edit-package/save-confirm.util";
import {
  CategoryAssignService,
  CategoryAssignSummary,
  hasEveryShelf,
  unwrittenSummary,
} from "./category-assign.service";
import { WriteScope } from "../write-scope";

export interface CategoryAssignApplied {
  summary: CategoryAssignSummary;
  addedShelves: ShelfRef[];
}

type AssignMode = "picker" | "tree";
type LoadState = "idle" | "loading" | "ready" | "error";

interface ModeOption {
  id: AssignMode;
  label: string;
  hint: string;
  icon: string;
}

const MODES: readonly ModeOption[] = [
  {
    id: "picker",
    label: "Search",
    hint: "Search the catalog and tick categories to add.",
    icon: "search",
  },
  {
    id: "tree",
    label: "Tree",
    hint: "Browse the full category hierarchy and tick to add.",
    icon: "account_tree",
  },
] as const;

/** Backend (`x-channel`) value for the AI Chatbot channel — the only channel
 *  where the catalog-first + separate-LifeStyle browsing model is surfaced (it
 *  mirrors the editor's Shelf tab). Off this channel the drawer keeps its
 *  single-feed `getCategories` load. */
/** Query that pulls every LifeStyle category straight from `GET /category` —
 *  they live outside every catalog, so the catalog-scoped feed never returns
 *  them. Built from the shared catalog-util constants so the characteristic
 *  name/value can't drift from the rest of the codebase. */
const LIFESTYLE_CATEGORY_QUERY =
  `categoryCharacteristic.name=${CATEGORY_TYPE_CHAR}` +
  `&categoryCharacteristic.value=${LIFESTYLE_CATEGORY_VALUE}` +
  `&page=1&pageSize=9999`;

/** Category-type filter for the AI Chatbot catalog (mirrors the Shelf tab). */
type CategoryTypeFilter = "lifestyle" | "regular";

interface TypeFilterOption {
  id: CategoryTypeFilter;
  label: string;
  icon?: string;
}

const TYPE_FILTER_OPTIONS: readonly TypeFilterOption[] = [
  { id: "regular", label: "Categories" },
  { id: "lifestyle", label: "LifeStyle", icon: "auto_awesome" },
] as const;

/** A catalog as returned by `GET /catalog` — only id + localized name are read,
 *  to populate the catalog picker that scopes the regular-category tree. */
interface CatalogRef {
  id: string;
  name?: LocalizedText;
}

/**
 * Add-only bulk shelf assignment for the list's selected prices. It owns its
 * catalog fetch and never touches the editor's `EnrichmentDataService` model,
 * so opening it from the list cannot disturb an edit session.
 */
@Component({
  selector: "app-enrichment-category-assign",
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, OmniarkUiModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: "./enrichment-category-assign.html",
  styleUrls: ["./enrichment-category-assign.scss"],
})
export class EnrichmentCategoryAssign implements OnInit {
  private readonly categorySvc = inject(CategoryService);
  private readonly catalogSvc = inject(CatalogService);
  private readonly assignSvc = inject(CategoryAssignService);
  private readonly languageMode = inject(LanguageModeService);
  private readonly permissionService = inject(PermissionService);
  private readonly featureFlags = inject(FeatureFlagService);
  private readonly productOfferingService = inject(ProductOfferingService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _rows = signal<EnrichmentPriceRow[]>([]);
  @Input() set rows(value: EnrichmentPriceRow[]) {
    this._rows.set(value ?? []);
  }
  get rows(): EnrichmentPriceRow[] {
    return this._rows();
  }
  private readonly _scope = signal<WriteScope | null>(null);
  @Input({ required: true }) set scope(value: WriteScope) {
    this._scope.set(value);
  }
  get channel(): string {
    return this._scope()?.channel ?? "";
  }

  @Output() applied = new EventEmitter<CategoryAssignApplied>();
  /** Emits when the user dismisses without applying. (Named `cancelled` rather
   *  than `cancel` to avoid shadowing the native DOM `cancel` event.) */
  @Output() cancelled = new EventEmitter<void>();

  /** How many target chips to render before collapsing into "+N more". */
  private static readonly TARGET_CHIP_LIMIT = 8;
  /** Cap on rendered picker rows — bounds DOM on large catalogs. */
  private static readonly PICKER_LIMIT = 80;

  // ── Catalog ────────────────────────────────────────────────────────────────
  protected readonly loadState = signal<LoadState>("idle");
  protected readonly loadError = signal<string | null>(null);

  /** Regular categories. Off AI Chatbot this is the single `getCategories`
   *  feed (legacy); on AI Chatbot it holds the SELECTED catalog's categories
   *  (catalog-first, via `getCategoryFromCatalog`). */
  protected readonly catalogTree = signal<ShelfCatalogNode[]>([]);

  /** LifeStyle categories — AI Chatbot only; fetched from the `categoryType`
   *  characteristic feed since they live outside every catalog. Empty on other
   *  channels (kept apart from `catalogTree` so switching the type filter swaps
   *  the displayed feed without discarding the other). */
  protected readonly lifeStyleTree = signal<ShelfCatalogNode[]>([]);

  /** Catalog picker (AI Chatbot only — the catalog-first step). */
  protected readonly catalogLoadState = signal<LoadState>("idle");
  protected readonly catalogs = signal<CatalogRef[]>([]);
  protected readonly selectedCatalogId = signal<string | null>(null);

  /** Monotonic request ids — discard stale async responses after the user
   *  flips catalog or type filter quickly. */
  private catalogRequestVersion = 0;
  private treeRequestVersion = 0;

  protected readonly selectedLocale = computed<EnrichmentLocale>(
    () => this.languageMode.activeLocale() as EnrichmentLocale,
  );

  /** The feed currently shown — the LifeStyle feed when its filter is active,
   *  the catalog (regular) feed otherwise. */
  protected readonly displayTree = computed<ShelfCatalogNode[]>(() =>
    this.effectiveTypeFilter() === "lifestyle"
      ? this.lifeStyleTree()
      : this.catalogTree(),
  );

  /** Both loaded feeds' roots — for id/name/lifestyle lookups that must stay
   *  valid regardless of which feed is on screen. */
  private readonly allLoadedRoots = computed<ShelfCatalogNode[]>(() => [
    ...this.catalogTree(),
    ...this.lifeStyleTree(),
  ]);

  catalogLabel(c: CatalogRef): string {
    return resolveLocalized(c.name, this.selectedLocale()) || c.id;
  }

  protected readonly catalogOptions = computed<SelectOption[]>(() =>
    this.catalogs().map((c) => ({ value: c.id, label: this.catalogLabel(c) })),
  );

  protected readonly flatCatalog = computed<ShelfCatalogEntry[]>(() =>
    flattenCatalog(this.displayTree(), this.selectedLocale()),
  );
  protected readonly catalogCount = computed(() => this.flatCatalog().length);
  private readonly lifeStyleIds = computed(() =>
    collectLifeStyleIds(this.allLoadedRoots()),
  );

  isLifeStyle(id: string): boolean {
    return this.lifeStyleIds().has(id);
  }

  // ── Category-type filter (AI Chatbot only) ───────────────────────────────────
  protected readonly typeFilterOptions = TYPE_FILTER_OPTIONS;
  protected readonly typeFilter = signal<CategoryTypeFilter>("regular");

  /** True only on the AI Chatbot channel, where the catalog-first +
   *  separate-LifeStyle model is surfaced — gates the picker + filter UI. */
  protected readonly isAiChatbotChannel = computed<boolean>(() =>
    isAiChatbotChannel(this.channel),
  );

  /** easyApp ignores `prices[].shelves` on save — its own price hook derives
   *  the link from category / subCategory and replaces the set (see
   *  {@link isEasyAppChannel}). A bulk assign there would return 200 and
   *  persist nothing the user picked, so the drawer refuses rather than
   *  reporting a success it can't stand behind. Shelf edits for this channel
   *  belong in the editor, where category / subCategory are authored. */
  protected readonly isEasyAppChannel = computed<boolean>(() =>
    isEasyAppChannel(this.channel),
  );

  /** The filter actually applied — forced to "regular" off AI Chatbot so a
   *  stale pick can never hide rows when the control isn't even shown (the
   *  LifeStyle feed only exists on AI Chatbot). */
  private readonly effectiveTypeFilter = computed<CategoryTypeFilter>(() =>
    this.isAiChatbotChannel() ? this.typeFilter() : "regular",
  );

  /** Per-id predicate for the active type filter (picker rows). Off the AI
   *  Chatbot channel there is no LifeStyle split, so every row passes. */
  private matchesTypeFilter(id: string): boolean {
    if (!this.isAiChatbotChannel()) return true;
    const isLife = this.lifeStyleIds().has(id);
    return this.typeFilter() === "lifestyle" ? isLife : !isLife;
  }

  setTypeFilter(id: CategoryTypeFilter): void {
    const prev = this.typeFilter();
    if (id === prev) return;
    this.typeFilter.set(id);
    // Crossing into / out of LifeStyle changes the data SOURCE (characteristic
    // feed vs the selected catalog), so re-fetch. all ↔ general is a pure
    // client-side prune over the already-loaded catalog feed — no fetch.
    if ((prev === "lifestyle") !== (id === "lifestyle")) {
      this.searchQuery.set("");
      this.expandedIds.set(new Set());
      this.loadActiveCategories();
    }
  }

  /** The rows, under every package, of the prices selected only off the
   *  channel. Such a row reads In design, yet another package that shares
   *  the price may have made it live, and a save lands on that overlay. */
  protected readonly liveCheck = signal<
    "checking" | "failed" | readonly EnrichmentPriceRow[]
  >("checking");

  protected readonly distinctPrices = computed<EnrichmentPriceRow[]>(() => {
    const rows = this._rows();
    const check = this.liveCheck();
    const selected = new Set(rows.map((row) => row.priceId));
    return pricesOf([
      ...rows,
      ...(typeof check === "string" ? [] : check).filter((row) =>
        selected.has(row.priceId),
      ),
    ]);
  });

  protected readonly priceCount = computed(() => this.distinctPrices().length);

  protected readonly packageCount = computed(
    () => new Set(this._rows().map((row) => row.offeringId)).size,
  );

  private readonly estimatedChangingPrices = computed(() => {
    const picked = this.toAddList();
    return this.distinctPrices().filter(
      (row) => !hasEveryShelf(row.shelves, picked),
    );
  });

  protected readonly mayKeepLiveOnSave = computed(() =>
    mayKeepLiveOnSave(this.permissionService, this.featureFlags),
  );

  protected readonly liveCopy = computed(() =>
    this.isEasyAppChannel()
      ? null
      : buildAssignLiveCopy(
          this.estimatedChangingPrices().filter((p) =>
            isRejectableStatus(p.status),
          ).length,
          { mayKeepLive: this.mayKeepLiveOnSave() },
        ),
  );

  protected readonly designedCopy = computed(() =>
    this.isEasyAppChannel()
      ? null
      : buildAssignDesignedCopy(
          this.estimatedChangingPrices().filter((p) =>
            isStageableStatus(p.status),
          ).length,
        ),
  );

  protected readonly uncheckedCount = computed(() =>
    this.liveCheck() === "failed"
      ? this.estimatedChangingPrices().filter((p) => !p.onChannel).length
      : 0,
  );

  protected readonly liveChoice = signal<AssignLiveChoice>("keep");

  private readonly boundEverywhereIds = computed<Set<string>>(() => {
    const perPrice = this.distinctPrices().map(
      (row) => new Set(row.shelves.map((s) => s.id)),
    );
    if (perPrice.length === 0) return new Set();
    const [first, ...rest] = perPrice;
    const result = new Set<string>();
    for (const id of first) {
      if (rest.every((s) => s.has(id))) result.add(id);
    }
    return result;
  });

  get visibleTargets(): EnrichmentPriceRow[] {
    return this.distinctPrices().slice(
      0,
      EnrichmentCategoryAssign.TARGET_CHIP_LIMIT,
    );
  }
  get hiddenTargetCount(): number {
    return Math.max(
      0,
      this.priceCount() - EnrichmentCategoryAssign.TARGET_CHIP_LIMIT,
    );
  }
  isBoundEverywhere(id: string): boolean {
    return this.boundEverywhereIds().has(id);
  }

  // ── Mode + search + tree state ───────────────────────────────────────────────
  protected readonly modes = MODES;
  protected readonly activeMode = signal<AssignMode>("picker");
  protected readonly activeModeOption = computed<ModeOption>(
    () => this.modes.find((m) => m.id === this.activeMode()) ?? this.modes[0],
  );
  protected readonly searchQuery = signal<string>("");
  protected readonly expandedIds = signal<Set<string>>(new Set<string>());

  // ── "To add" selection ───────────────────────────────────────────────────────
  protected readonly toAdd = signal<Map<string, ShelfRef>>(
    new Map<string, ShelfRef>(),
  );
  protected readonly toAddList = computed<ShelfRef[]>(() => [
    ...this.toAdd().values(),
  ]);
  protected readonly saving = signal(false);
  protected readonly canApply = computed(
    () =>
      this.toAdd().size > 0 &&
      !this.saving() &&
      this.priceCount() > 0 &&
      this.liveCheck() !== "checking" &&
      !this.isEasyAppChannel(),
  );

  ngOnInit(): void {
    this.checkLivePrices();
    // Catalog-first on every channel (mirrors the editor's Shelf tab): both
    // surfaces open on the Categories tab (the catalog feed) by default. AI
    // Chatbot additionally exposes a separate LifeStyle tab for the categories
    // that live outside catalogs.
    this.loadCatalogs();
  }

  // ── Catalog load ─────────────────────────────────────────────────────────────

  /** Step 1 (every channel) — fetch the channel's catalogs for the picker, then
   *  load the selected catalog's categories (catalog-first, mirroring the Shelf
   *  tab). Keeps the current selection if it survives the refresh, else defaults
   *  to the first catalog; settles on an empty pane when the channel has none. */
  private loadCatalogs(): void {
    const requestVersion = ++this.catalogRequestVersion;
    this.treeRequestVersion += 1; // invalidate any in-flight tree fetch
    this.catalogLoadState.set("loading");
    this.loadState.set("loading");
    this.loadError.set(null);
    this.lifeStyleTree.set([]);
    this.catalogSvc
      .getCatalogs(
        this.channel || null,
        "page=1&pageSize=9999&sort=name&sortOrder=asc",
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (requestVersion !== this.catalogRequestVersion) return;
          const list: CatalogRef[] = Array.isArray(res?.data)
            ? res.data
            : Array.isArray(res)
              ? res
              : [];
          this.catalogs.set(list);
          this.catalogLoadState.set("ready");
          const current = this.selectedCatalogId();
          const next =
            current && list.some((c) => c.id === current)
              ? current
              : (list[0]?.id ?? null);
          this.selectedCatalogId.set(next);
          this.loadActiveCategories();
        },
        error: (e) => {
          if (requestVersion !== this.catalogRequestVersion) return;
          this.catalogs.set([]);
          this.selectedCatalogId.set(null);
          this.catalogLoadState.set("error");
          this.loadError.set(this.errMessage(e, "Failed to load catalogs."));
          this.loadState.set("error");
        },
      });
  }

  /** Load the feed backing both modes for the current state: the LifeStyle
   *  characteristic feed when its filter is active, otherwise the selected
   *  catalog's categories. Settles on an empty pane when no catalog applies. */
  private loadActiveCategories(): void {
    if (this.effectiveTypeFilter() === "lifestyle") {
      this.loadLifeStyleCategories();
      return;
    }
    const id = this.selectedCatalogId();
    if (id) {
      this.loadCategoriesForCatalog(id);
    } else {
      this.catalogTree.set([]);
      this.loadState.set("ready");
    }
  }

  /** AI Chatbot step 2 — fetch the selected catalog's categories and rebuild
   *  the tree (`GET /category/catalog/{id}`). */
  private loadCategoriesForCatalog(catalogId: string): void {
    const requestVersion = ++this.treeRequestVersion;
    this.loadState.set("loading");
    this.loadError.set(null);
    this.categorySvc
      .getCategoryFromCatalog(catalogId, this.channel || null)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (requestVersion !== this.treeRequestVersion) return;
          this.catalogTree.set(this.toTree(this.parseCategories(res)));
          this.loadState.set("ready");
        },
        error: (e) => {
          if (requestVersion !== this.treeRequestVersion) return;
          this.loadError.set(
            this.errMessage(e, "Failed to load the catalog's categories."),
          );
          this.loadState.set("error");
        },
      });
  }

  /** AI Chatbot — fetch LifeStyle categories from the characteristic feed (they
   *  belong to no catalog, so `getCategoryFromCatalog` can't surface them). */
  private loadLifeStyleCategories(): void {
    const requestVersion = ++this.treeRequestVersion;
    this.loadState.set("loading");
    this.loadError.set(null);
    this.categorySvc
      .getCategories(this.channel || null, LIFESTYLE_CATEGORY_QUERY)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (requestVersion !== this.treeRequestVersion) return;
          this.lifeStyleTree.set(this.toTree(this.parseCategories(res)));
          this.loadState.set("ready");
        },
        error: (e) => {
          if (requestVersion !== this.treeRequestVersion) return;
          this.loadError.set(
            this.errMessage(e, "Failed to load LifeStyle categories."),
          );
          this.loadState.set("error");
        },
      });
  }

  /** Catalog picker change — re-scope the regular-category tree to the chosen
   *  catalog. No-op when unchanged. LifeStyle isn't catalog-scoped, so when that
   *  filter is active the selection is just remembered and applied on return. */
  onSelectCatalog(id: string): void {
    if (!id || id === this.selectedCatalogId()) return;
    this.selectedCatalogId.set(id);
    this.searchQuery.set("");
    this.expandedIds.set(new Set());
    if (this.effectiveTypeFilter() !== "lifestyle") {
      this.loadCategoriesForCatalog(id);
    }
  }

  /** Normalise a category payload to the FE tree. Tree-shaped input (roots with
   *  nested `subCategory[]`, no `parent.id` linkage) is used as-is; a flat list
   *  (entries reference a `parent.id` present in the list) is rebuilt so a node
   *  only appears once. Mirrors the Shelf tab — `getCategoryFromCatalog` can
   *  return either shape, and `buildCatalogTree` alone would drop children of an
   *  already-nested response. */
  private toTree(nodes: ShelfCatalogNode[]): ShelfCatalogNode[] {
    if (nodes.length === 0) return [];
    const ids = new Set(nodes.map((n) => n.id));
    const looksFlat = nodes.some((n) => n.parent?.id && ids.has(n.parent.id));
    return looksFlat ? buildCatalogTree(nodes) : nodes;
  }

  /** Pull the node list out of the dual-shaped category responses: the flat
   *  `{ data: [...] }` (getCategories) and the nested
   *  `{ data: { category | subCategory: [...] } }` (getCategoryFromCatalog),
   *  plus a bare array, defensively. */
  private parseCategories(res: unknown): ShelfCatalogNode[] {
    if (Array.isArray(res)) return res as ShelfCatalogNode[];
    const data = (res as { data?: unknown } | null | undefined)?.data;
    if (Array.isArray(data)) return data as ShelfCatalogNode[];
    const nested = data as
      { category?: unknown; subCategory?: unknown } | null | undefined;
    if (Array.isArray(nested?.category))
      return nested.category as ShelfCatalogNode[];
    if (Array.isArray(nested?.subCategory))
      return nested.subCategory as ShelfCatalogNode[];
    return [];
  }

  private errMessage(e: unknown, fallback: string): string {
    const err = e as
      { error?: { message?: string }; message?: string } | null | undefined;
    return err?.error?.message || err?.message || fallback;
  }

  /** Retry hook for the error pane. If the catalog LIST failed, restart there;
   *  otherwise just re-run the active feed (LifeStyle / selected catalog / empty
   *  pane) so a transient failure recovers without a full reload. */
  retryLoadCatalog(): void {
    if (this.catalogLoadState() !== "ready") {
      this.loadCatalogs();
    } else {
      this.loadActiveCategories();
    }
  }

  setMode(id: AssignMode): void {
    this.activeMode.set(id);
  }

  // ── Picker mode ──────────────────────────────────────────────────────────────
  private readonly pickerMatches = computed<ShelfCatalogEntry[]>(() => {
    const q = this.searchQuery().trim().toLowerCase();
    const typed = this.flatCatalog().filter((e) =>
      this.matchesTypeFilter(e.id),
    );
    if (!q.length) return typed;
    return typed.filter(
      (e) =>
        e.id.toLowerCase().includes(q) ||
        e.name.toLowerCase().includes(q) ||
        e.pathLabel.toLowerCase().includes(q),
    );
  });
  protected readonly pickerResults = computed<ShelfCatalogEntry[]>(() =>
    this.pickerMatches().slice(0, EnrichmentCategoryAssign.PICKER_LIMIT),
  );
  protected readonly pickerMatchCount = computed(
    () => this.pickerMatches().length,
  );
  protected readonly pickerOverflowCount = computed(() =>
    Math.max(0, this.pickerMatchCount() - this.pickerResults().length),
  );
  protected readonly pickerHasResults = computed(
    () => this.pickerResults().length > 0,
  );

  onSearchChange(value: string): void {
    this.searchQuery.set(value);
  }
  clearSearch(): void {
    this.searchQuery.set("");
  }

  // ── Tree mode ────────────────────────────────────────────────────────────────

  /** The displayed feed narrowed by the active type filter. Keeps any node
   *  matching the wanted type plus the ancestor chain of a deeper match so the
   *  path stays navigable; non-matching leaves drop. Untouched off AI Chatbot. */
  protected readonly filteredTree = computed<ShelfCatalogNode[]>(() => {
    if (!this.isAiChatbotChannel()) return this.displayTree();
    const wantLife = this.typeFilter() === "lifestyle";
    const prune = (nodes: ShelfCatalogNode[]): ShelfCatalogNode[] => {
      const out: ShelfCatalogNode[] = [];
      for (const n of nodes) {
        const kids = n.subCategory?.length ? prune(n.subCategory) : [];
        const selfMatch = isLifeStyleNode(n) === wantLife;
        if (selfMatch || kids.length) out.push({ ...n, subCategory: kids });
      }
      return out;
    };
    return prune(this.displayTree());
  });

  protected readonly treeRootCount = computed(() => this.filteredTree().length);

  isExpanded(id: string): boolean {
    return this.expandedIds().has(id);
  }
  toggleExpand(id: string): void {
    this.expandedIds.update((set) => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  expandAll(): void {
    const ids = new Set<string>();
    const walk = (nodes: ShelfCatalogNode[]): void => {
      for (const n of nodes) {
        if (n.subCategory?.length) {
          ids.add(n.id);
          walk(n.subCategory);
        }
      }
    };
    walk(this.filteredTree());
    this.expandedIds.set(ids);
  }
  collapseAll(): void {
    this.expandedIds.set(new Set());
  }

  resolveName(node: ShelfCatalogNode): string {
    return resolveLocalized(node.name, this.selectedLocale()) || node.id;
  }
  isLifeStyleNode(node: ShelfCatalogNode): boolean {
    return isLifeStyleNode(node);
  }

  private hasSelectedDescendant(node: ShelfCatalogNode): boolean {
    const sel = this.toAdd();
    const walk = (n: ShelfCatalogNode): boolean => {
      if (!n.subCategory) return false;
      for (const c of n.subCategory) {
        if (sel.has(c.id)) return true;
        if (walk(c)) return true;
      }
      return false;
    };
    return walk(node);
  }
  nodeSelectState(node: ShelfCatalogNode): "checked" | "indeterminate" | "off" {
    if (this.toAdd().has(node.id)) return "checked";
    if (this.hasSelectedDescendant(node)) return "indeterminate";
    return "off";
  }
  onToggleNode(node: ShelfCatalogNode, checked: boolean): void {
    if (checked) this.addToSelection(this.nodeToShelfRef(node));
    else this.removeFromSelection(node.id);
  }

  // ── Selection write helpers ──────────────────────────────────────────────────
  isSelected(id: string): boolean {
    return this.toAdd().has(id);
  }

  /** Picker-row label resolved against the active language (chip / row text). */
  shelfLabel(shelf: ShelfRef): string {
    return resolveLocalized(shelf.__name ?? shelf.name, this.selectedLocale());
  }

  toggleEntry(entry: ShelfCatalogEntry): void {
    if (this.isSelected(entry.id)) this.removeFromSelection(entry.id);
    else this.addToSelection(this.entryToShelfRef(entry));
  }

  removeFromSelection(id: string): void {
    this.toAdd.update((map) => {
      if (!map.has(id)) return map;
      const next = new Map(map);
      next.delete(id);
      return next;
    });
  }

  private addToSelection(shelf: ShelfRef): void {
    this.toAdd.update((map) => {
      if (map.has(shelf.id)) return map;
      const next = new Map(map);
      next.set(shelf.id, shelf);
      return next;
    });
  }

  /** Catalog entry → ShelfRef. `name` pins the default-locale string (stable
   *  for the PATCH body / list grid); `__name` carries the locale map so the
   *  chip can re-resolve per active language. Mirrors the Shelf tab. */
  private entryToShelfRef(entry: ShelfCatalogEntry): ShelfRef {
    return {
      id: entry.id,
      name: resolveLocalized(entry.nameI18n) || entry.name,
      __name: entry.nameI18n,
    };
  }
  private nodeToShelfRef(node: ShelfCatalogNode): ShelfRef {
    return {
      id: node.id,
      name: resolveLocalized(node.name) || node.id,
      __name: node.name,
    };
  }

  clearSelection(): void {
    this.toAdd.set(new Map());
  }

  private checkLivePrices(): void {
    const offChannel = pricesOf(this._rows()).filter((row) => !row.onChannel);
    if (offChannel.length === 0 || !this.channel || this.isEasyAppChannel()) {
      this.liveCheck.set([]);
      return;
    }
    readPriceRows(
      this.productOfferingService,
      offChannel.map((row) => row.priceId),
      this.channel,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows) => this.liveCheck.set(rows),
        error: () => this.liveCheck.set("failed"),
      });
  }

  // ── Apply / cancel ───────────────────────────────────────────────────────────
  apply(): void {
    const scope = this._scope();
    // Re-check the channel gate here too, not just in canApply: a 200 that
    // persisted nothing is worse than a disabled button.
    if (!this.canApply() || !scope?.channel || this.isEasyAppChannel()) return;
    const addedShelves = this.toAddList();
    const rows = this.distinctPrices();
    const decision = resolveAssignDecision({
      mayKeepLive: this.mayKeepLiveOnSave(),
      picked: this.liveCopy()?.mode === "choose" ? this.liveChoice() : null,
    });
    this.saving.set(true);
    this.assignSvc
      .assignShelves(rows, addedShelves, scope, decision)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (summary) => {
          this.saving.set(false);
          this.applied.emit({ summary, addedShelves });
        },
        error: (error) => {
          // The service captures per-package errors, so reaching here means a
          // pipeline-level failure — surface it as an all-failed summary so the
          // host still closes the drawer and tells the user nothing persisted.
          this.saving.set(false);
          this.applied.emit({
            summary: unwrittenSummary(rows, error),
            addedShelves,
          });
        },
      });
  }

  onCancel(): void {
    if (this.saving()) return;
    this.cancelled.emit();
  }

  // ── trackBy ──────────────────────────────────────────────────────────────────
  trackByMode = (_: number, m: ModeOption): string => m.id;
  trackByEntryId = (_: number, e: ShelfCatalogEntry): string => e.id;
  trackByNodeId = (_: number, n: ShelfCatalogNode): string => n.id;
  trackByShelfId = (_: number, s: ShelfRef): string => s.id;
  trackByPriceId = (_: number, row: EnrichmentPriceRow): string => row.priceId;
}
